import { StandardEvents } from '@shopify/events';

// Always use the authoritative cart, not the add response's quantity delta.
// Fee lines must never inflate the cabinet/item count in the header.
export function merchandiseCount(cart) {
  if (!Array.isArray(cart?.items)) throw new Error('Invalid cart response');
  return cart.items.reduce((count, item) => item.product_type === 'Fee' ? count : count + item.quantity, 0);
}

export function merchandiseLines(cart) {
  return (cart?.items || []).filter(item => item.product_type !== 'Fee');
}

// Compare the Ajax cart with the server-rendered Clay-verified cart before
// exposing any price. The fee reconciler can briefly leave /cart.js behind.
export function cartSnapshot(cart) {
  return (cart?.items || []).map(item => ({
    key: String(item.key), qty: Number(item.quantity), line: Number(item.final_line_price),
  })).sort((a, b) => a.key.localeCompare(b.key));
}

export function verifiedCartTotal(cart, summaryNodes = []) {
  const actual = JSON.stringify(cartSnapshot(cart));
  const cents = Number(cart?.total_price);
  if (!Number.isSafeInteger(cents) || cents < 0) return null;
  for (const node of summaryNodes) {
    if (Number(node?.dataset?.treeCartTotalCents) !== cents) continue;
    try {
      const saved = JSON.parse(node.dataset.treeCartSnapshot).map(item => ({
        key: String(item.key), qty: Number(item.qty), line: Number(item.line),
      })).sort((a, b) => a.key.localeCompare(b.key));
      if (JSON.stringify(saved) === actual) return cents;
    } catch { /* A missing or stale verified summary is not a price source. */ }
  }
  return null;
}

export function verifiedDealerSubtotal(cart, summaryNodes = []) {
  const cents = merchandiseLines(cart).reduce((sum, item) => sum + Number(item.final_line_price), 0);
  return verifiedCartTotal({ ...cart, total_price: cents }, Array.from(summaryNodes).map(node => ({
    dataset: { treeCartSnapshot: node.dataset.treeCartSnapshot, treeCartTotalCents: node.dataset.treeDealerSubtotalCents },
  })));
}

export function setupCartFeedback(doc = document, win = window, request = fetch) {
  let version = 0;
  let controller;
  let feedbackTimer;
  let totalPollTimer;
  let totalPollAttempts = 0;
  let animation;
  const peek = doc.querySelector('[data-tree-cart-peek]');
  const cartLink = doc.querySelector('[data-tree-cart]');
  let savedOverflow;
  let remoteSummary = [];

  function hidePreview() {
    ++version;
    controller?.abort();
    win.clearTimeout(totalPollTimer);
    if (peek) {
      if (peek.open) peek.close();
      peek.hidden = true;
    }
    if (savedOverflow !== undefined && doc.body) {
      doc.body.style.overflow = savedOverflow;
      savedOverflow = undefined;
    }
    cartLink?.setAttribute('aria-expanded', 'false');
  }

  function showPreview(cart, lines, link, count, polling = false) {
    if (!peek || !doc.createElement) return false;
    const items = merchandiseLines(cart);
    if (!items.length) return false;
    const itemHost = peek.querySelector('[data-tree-cart-peek-items]');
    const countHost = peek.querySelector('[data-tree-cart-peek-count]');
    const totalHost = peek.querySelector('[data-tree-cart-peek-total]');
    const noteHost = peek.querySelector('[data-tree-cart-peek-note]');
    if (!itemHost || !countHost || !totalHost || !noteHost) return false;

    const summaries = [...(doc.querySelectorAll?.('[data-tree-cart-snapshot]') || []), ...remoteSummary];
    const verifiedTotal = verifiedCartTotal(cart, summaries);
    const subtotal = verifiedTotal === null ? verifiedDealerSubtotal(cart, summaries) : null;
    const displayTotal = verifiedTotal ?? subtotal;
    const labelHost = peek.querySelector('[data-tree-cart-peek-total-label]');
    if (labelHost) labelHost.textContent = subtotal === null ? 'Cart total' : 'Dealer subtotal';
    const money = cents => new Intl.NumberFormat(doc.documentElement?.lang || 'en-US', {
      style: 'currency', currency: cart.currency || 'USD',
    }).format(cents / 100);
    const addedIds = new Set(lines.map(line => String(line?.merchandiseId || '').split('/').pop()));

    const rows = items.map(item => {
      const row = doc.createElement('div');
      row.className = `tc-cart-peek__item${addedIds.has(String(item.variant_id)) ? ' tc-cart-peek__item--added' : ''}`;
      const imageBox = doc.createElement('div');
      imageBox.className = 'tc-cart-peek__image';
      const source = item.featured_image?.url || item.image;
      let safeSource;
      if (typeof source === 'string' && source.trim()) {
        try {
          const url = new URL(source, win.location.origin);
          if (url.protocol === 'https:' || url.protocol === 'http:') safeSource = url.href;
        } catch { /* Some products have no image. */ }
      }
      if (safeSource) {
        const image = doc.createElement('img');
        image.src = safeSource;
        image.alt = '';
        image.width = 52;
        image.height = 52;
        imageBox.append(image);
      } else {
        imageBox.textContent = 'TC';
      }

      const copy = doc.createElement('div');
      copy.className = 'tc-cart-peek__item-copy';
      const title = doc.createElement('strong');
      title.className = 'tc-cart-peek__item-title';
      title.textContent = item.product_title || item.title || item.sku || 'Cabinet';
      const meta = doc.createElement('span');
      meta.className = 'tc-cart-peek__item-meta';
      meta.textContent = `${item.sku ? `SKU ${item.sku} · ` : ''}Qty ${item.quantity}`;
      copy.append(title, meta);
      const price = doc.createElement('span');
      price.className = 'tc-cart-peek__item-price';
      price.textContent = displayTotal === null ? '—' : money(item.final_line_price);
      row.append(imageBox, copy, price);
      return row;
    });
    const scrollTop = polling ? itemHost.scrollTop : 0;
    itemHost.replaceChildren(...rows);
    itemHost.scrollTop = scrollTop;
    countHost.textContent = `${count} ${count === 1 ? 'unit' : 'units'} · ${items.length} ${items.length === 1 ? 'line' : 'lines'}`;
    totalHost.textContent = displayTotal === null ? 'Open cart for total' : money(displayTotal);
    noteHost.textContent = displayTotal === null
      ? 'Dealer prices are being verified. View the cart to review your total.'
      : subtotal !== null ? 'Cabinets only. Assembly, modifications and other fees are finalized in the cart; shipping and tax at checkout.'
      : 'Includes current fees. Shipping and tax are calculated at checkout.';
    peek.hidden = false;
    if (!peek.open && peek.showModal) {
      if (doc.body && savedOverflow === undefined) {
        savedOverflow = doc.body.style.overflow;
        doc.body.style.overflow = 'hidden';
      }
      peek.showModal();
    }
    link.setAttribute('aria-expanded', 'true');
    if (!polling) {
      totalPollAttempts = 0;
    }
    win.clearTimeout(totalPollTimer);
    if (displayTotal === null && totalPollAttempts < 4) {
      totalPollAttempts += 1;
      totalPollTimer = win.setTimeout(() => { if (!peek.hidden) refresh(false, [], true, true); }, 700);
    }
    return true;
  }

  async function refresh(announce = true, addedLines = [], openPreview = false, polling = false) {
    const link = doc.querySelector('[data-tree-cart]');
    if (!link) return; // Keep guests and pending dealers restricted.
    const revision = ++version;
    controller?.abort();
    controller = new AbortController();
    try {
      const url = new URL(link.href, win.location.origin);
      url.pathname = url.pathname.replace(/\/$/, '') + '.js';
      url.search = '';
      const response = await request(url.href, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Cart count unavailable');
      const cart = await response.json();
      const count = merchandiseCount(cart);
      if (revision !== version || !link.isConnected) return;
      const previous = Number(link.dataset.count || 0);
      const badge = link.querySelector('[data-tree-cart-count]');
      if (!badge) return;
      badge.textContent = String(count);
      badge.hidden = count === 0;
      link.dataset.count = String(count);
      const label = `Cart, ${count} ${count === 1 ? 'item' : 'items'}`;
      link.setAttribute('aria-label', label);
      const status = doc.querySelector('[data-tree-cart-status]');
      if (announce && count !== previous && status) status.textContent = count > previous ? `Added to cart. ${label}.` : `${label}.`;
      if (count === 0) hidePreview();
      const added = announce && count > previous;
      const feedback = link.querySelector('[data-tree-cart-feedback]');
      if (count && (openPreview || (peek && !peek.hidden))) {
        if (win.DOMParser) {
          try {
            const summaryUrl = new URL(link.href, win.location.origin);
            summaryUrl.search = '?section_id=tree-cart-peek-data';
            const rendered = await request(summaryUrl.href, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
            if (rendered.ok) {
              const parsed = new win.DOMParser().parseFromString(await rendered.text(), 'text/html');
              remoteSummary = [...parsed.querySelectorAll('[data-tree-cart-snapshot]')];
            } else remoteSummary = [];
          } catch (error) { remoteSummary = []; if (error.name === 'AbortError') return; }
        }
        if (revision !== version) return;
        showPreview(cart, addedLines, link, count, polling);
      }
      if (added && feedback) {
        win.clearTimeout(feedbackTimer);
        feedback.hidden = false;
        feedbackTimer = win.setTimeout(() => { feedback.hidden = true; }, 2200);
      }
      if (!added) return;
      animation?.cancel();
      if (!win.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        animation = badge.animate?.([
          { transform: 'scale(1)' }, { transform: 'scale(1.28)', offset: .4 }, { transform: 'scale(1)' },
        ], { duration: 380, easing: 'ease-out' });
      }
    } catch (error) {
      // Never clear a known count or show success when the cart read fails.
      if (error.name !== 'AbortError') console.warn('[Tree cart] Could not refresh the cart count.');
    }
  }

  const onUpdate = event => {
    if (!event.promise?.then) return;
    const lines = event.action === 'add' ? event.lines || [] : [];
    event.promise.then(result => refresh(!result?.detail?.didError, lines)).catch(() => {});
  };
  const onPageShow = event => { if (event.persisted) refresh(false); };
  const onClick = event => {
    const trigger = event.target?.closest?.('[data-tree-cart]');
    if (trigger && !event.defaultPrevented && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && Number(trigger.dataset.count) > 0) {
      event.preventDefault();
      if (peek && !peek.hidden) hidePreview();
      else refresh(false, [], true);
      return;
    }
    if (event.target?.closest?.('[data-tree-cart-peek-close]')) hidePreview();
    else if (event.target === peek) {
      const rect = peek.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) hidePreview();
    }
    else if (peek && !peek.hidden && !peek.contains(event.target) && !event.target?.closest?.('[data-tree-cart]')) hidePreview();
  };
  const onKeyDown = event => { if (event.key === 'Escape') hidePreview(); };
  const onCancel = event => { event.preventDefault(); hidePreview(); };
  peek?.addEventListener('cancel', onCancel);
  peek?.addEventListener('close', hidePreview);
  doc.addEventListener(StandardEvents.cartLinesUpdate, onUpdate);
  doc.addEventListener('click', onClick);
  doc.addEventListener('keydown', onKeyDown);
  win.addEventListener('pageshow', onPageShow);
  return { refresh, destroy() {
    ++version;
    controller?.abort();
    animation?.cancel();
    win.clearTimeout(feedbackTimer);
    hidePreview();
    doc.removeEventListener(StandardEvents.cartLinesUpdate, onUpdate);
    doc.removeEventListener('click', onClick);
    doc.removeEventListener('keydown', onKeyDown);
    win.removeEventListener('pageshow', onPageShow);
    peek?.removeEventListener('cancel', onCancel);
    peek?.removeEventListener('close', hidePreview);
  } };
}

if (typeof document !== 'undefined') setupCartFeedback();
